/**
 * Menus and toolbar buttons as data, like Unreal Engine's tool menus: nobody owns the menu bar
 * or the toolbar. Each service contributes its own lines next to its own code (`projectMenus`
 * in `state/project.ts`, and so on), each subsystem hands them over (`menus` and `toolbar` in
 * `app/subsystems.ts`), and the shell service (`state/shell.ts`) gathers them, section by
 * section, into the menu bar's menus (File, Edit, View, Window, Tools, Help), the toolbar under
 * it, and plugins add theirs the same way.
 *
 * A menu line is the same {@link MenuEntry} the right-click menus use: a label and an action or
 * a named command (whose shortcut is then shown).
 */

/** One clickable line (or a line opening a sub-menu). */
export interface MenuItem {
  /** What the line says. */
  label: string;
  /** What a click does. */
  action?: () => void;
  /** A named command to run instead (see `shared/commands.ts`); its shortcut is shown. */
  command?: string;
  /** The command's argument, if it takes one. */
  argument?: string | null;
  /** Greyed out and not clickable. */
  disabled?: boolean;
  /** Shown in red (deletes or replaces something). */
  danger?: boolean;
  /** Asks to be clicked twice: the first click shows this text instead and keeps the menu open. */
  confirm?: string;
  /** Shows a check mark (on) or an empty box (off); absent: neither. */
  checked?: boolean;
  /** A sub-menu, opened on hover or with the right arrow key. */
  items?: MenuEntry[];
}

/** A line of a menu: an item, a separator, or a small title. */
export type MenuEntry = MenuItem | { separator: true } | { heading: string };

/** A separator line. */
export const SEPARATOR: MenuEntry = { separator: true };

/** `true` if the entry is a clickable item. */
export const isItem = (entry: MenuEntry): entry is MenuItem => "label" in entry;

/** Drops separators at the ends and doubled ones, and headings with nothing under them, so entries can be built carelessly. */
export function tidyEntries(entries: MenuEntry[]): MenuEntry[] {
  const kept = entries.filter((e, i, all) => {
    if (!("heading" in e)) return true;
    const next = all[i + 1];
    return !!next && isItem(next);
  });
  return kept.filter((e, i, all) => !("separator" in e) || (i > 0 && i < all.length - 1 && !("separator" in all[i - 1]!)));
}

/** The menus of the menu bar, in their order (Unreal's: File, Edit, Window, Tools, Help; plus View). */
export const MENU_BAR = [
  { id: "file", label: "File" },
  { id: "edit", label: "Edit" },
  { id: "view", label: "View" },
  { id: "window", label: "Window" },
  { id: "tools", label: "Tools" },
  { id: "help", label: "Help" },
] as const;

/** A menu of the menu bar. */
export type MenuBarId = (typeof MENU_BAR)[number]["id"];

/**
 * A section of a menu bar's menu, contributed by a service: its title (shown as a small heading,
 * as Unreal does) and its lines, asked for each time the menu opens (so they follow the state).
 */
export interface MenuSection {
  /** The menu it goes in. */
  menu: MenuBarId;
  /** Its title, shown above its lines ("Project", "History"...). Sections with the same title join. */
  section: string;
  /** Where the section goes in the menu: lower first (default 50). */
  order?: number;
  /** Its lines, now. */
  entries: () => MenuEntry[];
}

/** The pictures a toolbar button can show (`shared/icons.ts`). */
export type IconName = "save" | "undo" | "redo" | "add" | "link" | "presets" | "scan" | "ai" | "plugins" | "fit" | "diagram" | "cube" | "windows" | "log" | "search";

/** A toolbar button, contributed by a service. */
export interface ToolbarButton {
  /** Its stable id, also its DOM id (`save`, `undo`). */
  id: string;
  /** The group it is drawn in; groups are separated by a line. */
  section: string;
  /** Where its group goes: lower first (default 50). Buttons keep their order inside it. */
  order?: number;
  /** Its picture. */
  icon: IconName;
  /** The word under or beside its picture. */
  label: string;
  /** The tooltip (the command's shortcut is added to it). */
  title?: string;
  /** What a click does. */
  action?: () => void;
  /** A named command to run instead; its shortcut is shown in the tooltip. */
  command?: string;
  /** The command's argument. */
  argument?: string | null;
  /** Greyed out now. */
  disabled?: () => boolean;
  /** Shown pressed now (a switch that is on). */
  pressed?: () => boolean;
}

/** The lines of menu `menu` from `sections`: sections in order, each under its title, separated by lines. */
export function menuOf(sections: readonly MenuSection[], menu: MenuBarId): MenuEntry[] {
  const mine = sections.filter((s) => s.menu === menu);
  const titles: { section: string; order: number }[] = [];
  for (const s of mine) {
    if (!titles.some((t) => t.section === s.section)) titles.push({ section: s.section, order: s.order ?? 50 });
  }
  titles.sort((a, b) => a.order - b.order);
  const entries: MenuEntry[] = [];
  for (const { section } of titles) {
    const lines = mine.filter((s) => s.section === section).flatMap((s) => s.entries());
    if (!lines.some(isItem)) continue;
    entries.push(SEPARATOR, { heading: section }, ...lines);
  }
  return tidyEntries(entries);
}

/** The toolbar's buttons in groups, groups in order (a group keeps the order buttons came in). */
export function toolbarGroups(buttons: readonly ToolbarButton[]): ToolbarButton[][] {
  const groups: { section: string; order: number; buttons: ToolbarButton[] }[] = [];
  for (const b of buttons) {
    const group = groups.find((g) => g.section === b.section);
    if (group) group.buttons.push(b);
    else groups.push({ section: b.section, order: b.order ?? 50, buttons: [b] });
  }
  return groups.sort((a, b) => a.order - b.order).map((g) => g.buttons);
}

/** The groups of the Window menu, in order (a window names its own; none: "Other"). */
export const WINDOW_GROUPS = ["Graph", "Project", "Together", "Plugins", "App"];
